import { useState, useMemo } from 'react'
import { makeStyles, Typography } from '@material-ui/core'
import moment from 'moment'
import find from 'lodash/find'
import sortBy from 'lodash/sortBy'
import difference from 'lodash/difference'

import SecurityAdministrationLayout from '../../../commons/SecurityAdministrationLayout'
import GroupToRolesAndUsersForm from './Form'
import ResultsTable from 'components/ResultsTable'
import CollapseDetails from 'components/commons/CollapsibleList'
import NotRegisterEmailsModal from '../GroupNotRegisterModal'

import { assignGroupToRolesAndUsersApi } from 'actions/groupActions'
import { useStateValue } from 'context/store'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'
import TRANSLATE from 'utils/constants'
import httpClient from 'services/HttpClient'
import { closeSpinnerAction, openSpinnerAction } from 'context/reducers/openSpinnerReducer'
import { initialModalState } from 'components/commons/PortalModal'
import { openModalAction } from 'context/reducers/modalReducer'

const useRowStyles = makeStyles({
   titleOne: {
      margin: '20px 0 15px 9px',
      fontStyle: 'normal',
      fontSize: 14,
      color: '#8A8FAB',
      fontWeight: 400,
      textAlign: 'center'
   }
})

const initialFormState = {
   groupId: [],
   usersId: [],
   massiveUsersMode: true
}

const getUserName = u => `${u.documentType}${u.documentNumber} - ${u.name} ${u.lastName}`
const groupsNameText = groups => groups?.map(gr => gr?.name).join(' - ')

const formatData = (assignedGroupsData = [], formData) => {
   const { usersId, groupId } = formData
   const usersNames = usersId?.map(user => getUserName(user)) ?? []
   const ids = usersId?.map(user => user?.id) ?? []

   const tableData = []
   for (const assignedGroupData of assignedGroupsData) {
      const { group, users: groupUsers } = assignedGroupData
      const { id, name } = group

      const filteredUsers = groupUsers.filter(user => ids?.includes(user?.id) && user?.relationshipDate)
      const recentRelatedUser = sortBy(filteredUsers, 'relationshipDate').reverse()[0] ?? {}

      const { relationshipBy, relationshipDate } = recentRelatedUser
      const date = moment(relationshipDate)
      const createdDate = date.isValid() ? date.format('DD/MM/YYYY hh:mm a') : ''

      tableData.push({
         id,
         name,
         rol: find(groupId, { id })?.roles,
         users: usersNames,
         createdBy: relationshipBy,
         createdDate
      })
   }
   return tableData
}

const GroupToRolesAndUsers = () => {
   const classes = useRowStyles()
   const [_, dispatch] = useStateValue()
   const [isPopoverOpen, setIsPopoverOpen] = useState(true)
   const [assignedGroupData, setAssignedGroupData] = useState([])
   const [data, setData] = useState(initialFormState)

   const columns = useMemo(
      () => [
         { title: 'ID', field: 'id', align: 'center' },
         { title: 'GRUPO', field: 'name', align: 'center' },
         {
            title: 'ROL',
            field: 'rol',
            align: 'center',
            render: rowData => <CollapseDetails data={rowData?.rol} />
         },
         {
            title: 'USUARIO',
            field: 'users',
            align: 'center',
            render: rowData => <CollapseDetails data={rowData?.users} getItemLabel={item => item} />
         },
         { title: 'FECHA RELACIÓN', field: 'createdDate', align: 'center' },
         { title: 'USUARIO RELACIÓN', field: 'createdBy', align: 'center' }
      ],
      []
   )

   const getGroupData = async group => {
      const url = `${process.env.REACT_APP_GROUPS_API}/groups/${group?.id}`
      return await httpClient.get(url)
   }

   const getUsersFromEmail = async emails => {
      const url = `${process.env.REACT_APP_USERS_API}/users/users-by-email`
      return await httpClient.post(url, { emails })
   }

   const massiveRegisteredUsers = async formData => {
      const { massiveUsersMode, usersId } = formData
      let notRegisteredEmails = []

      if (!massiveUsersMode) return [formData, notRegisteredEmails]
      else {
         try {
            const registeredUsers = await getUsersFromEmail(usersId)
            notRegisteredEmails = difference(
               usersId,
               registeredUsers?.data?.map(usr => usr?.email)
            )
            return [{ ...formData, usersId: registeredUsers?.data }, notRegisteredEmails]
         } catch (err) {
            showMessage(err?.response?.data?.message || `Ha sucedido un error en el servidor`)
            return [{ ...formData, usersId: [] }, notRegisteredEmails]
         }
      }
   }

   const assignGroupToRolesAndUsers = async formValues => {
      dispatch(openSpinnerAction())
      const [formData, notRegisterUsers] = await massiveRegisteredUsers(formValues)

      // setIsPopoverOpen(true)
      setAssignedGroupData([])

      if (!formData?.usersId?.length) {
         dispatch(closeSpinnerAction())
         return false
      }

      const savedGroups = []
      const errorGroups = []

      for (const group of formData.groupId) {
         const notPreviousGroupUsers = []
         try {
            const groupData = await getGroupData(group)
            const previousGroupUsers = groupData?.data?.users ?? []
            for (const user of formData?.usersId) {
               const id = user?.id
               const userInGroup = find(previousGroupUsers, { id })
               if (!userInGroup) notPreviousGroupUsers.push(id)
            }

            if (notPreviousGroupUsers.length) {
               const assignedGroupData = await assignGroupToRolesAndUsersApi(group?.id, {
                  usersId: notPreviousGroupUsers
               })
               savedGroups.push(assignedGroupData)
            } else savedGroups.push({ ...groupData?.data, group: { id: group?.id, name: group?.name } })
         } catch (error) {
            errorGroups.push(group)
         }
      }

      if (savedGroups.length) {
         const formattedData = formatData(savedGroups, formData)
         setData(initialFormState)
         setIsPopoverOpen(false)
         setAssignedGroupData({ data: formattedData })
      }
      resultMessages(savedGroups, errorGroups, formData)
      notRegisterUsers?.length && dispatch(
         openModalAction({
            ...initialModalState,
            iconColor: 'info',
            confirmButtonText: 'Entendido',
            hideCloseButton: true,
            message: <NotRegisterEmailsModal emails={notRegisterUsers} />
         })
      )
      dispatch(closeSpinnerAction())
      return true
   }

   const showMessage = (message, severity) => dispatch(openSnackBarAction(message, severity))

   const resultMessages = (savedGroups, errorGroups, formData) => {
      if (!savedGroups.length) return showMessage('Error al relacionar grupo a usuarios. Intenta nuevamente.')
      if (formData?.groupId.length == savedGroups.length)
         return showMessage('Usuarios relacionados correctamente a l(os) grupo(s).', 'success')
      showMessage(
         `La relación no se realizó completamente, los siguientes grupos presentaron fallas: 
               ${groupsNameText(errorGroups)}. Por favor valide y realice nuevamente el proceso.`,
         'warning'
      )
      return true
   }

   return (
      <SecurityAdministrationLayout
         form={
            <GroupToRolesAndUsersForm
               assignGroupToRolesAndUsers={assignGroupToRolesAndUsers}
               data={data}
               setData={setData}
            />
         }
         table={
            assignedGroupData?.data?.length ? (
               <ResultsTable columns={columns} tableData={assignedGroupData} customActions={[]} />
            ) : (
               <Typography className={classes.titleOne}>{TRANSLATE.LABELS.NO_RESULTS}</Typography>
            )
         }
         title={TRANSLATE.LABELS.LINK_GROUPS_TO_ROLES_AND_USERS}
         popoverProp={isPopoverOpen}
         setPopoverProp={value => setIsPopoverOpen(value)}
      />
   )
}

export default GroupToRolesAndUsers
